import React, { useState, useEffect } from 'react';
import { ArrowRight, Clock, Check, Lightbulb, Loader2 } from 'lucide-react';
import { useTheme } from '../../utils/ThemeSwitcher';
import { fetchLocalProjects, activateProjectStorage } from '../../utils/localProjects';
import './WelcomeLanding.css';
 
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
 
// ---------------------------------------------------------------------------
// Stage definitions — mirror the target keys used elsewhere in the app
// ---------------------------------------------------------------------------
const WORKFLOW_STAGES = [
  { label: 'Setup',   hint: 'project',          target: 'projects',         tip: 'Create or pick the home for your survey data, checks, and exports.' },
  { label: 'Load',    hint: 'kobo / cto / odk', target: 'primary-survey',   tip: 'Connect KoBo, SurveyCTO, ODK, or upload a CSV. We handle the schema.' },
  { label: 'Analyse', hint: '9 detectors',      target: 'quality-checks',   tip: 'Run all 9 quality detectors — completeness, duplicates, outliers, flow, GPS…' },
  { label: 'Clean',   hint: 'studio',           target: 'cleaning-studio',  tip: 'Fix flagged rows in the Cleaning Studio. Every edit is tracked & reversible.' },
  { label: 'Export',  hint: 'csv / report',     target: 'data-export',      tip: 'Export a clean CSV or a full quality report with before/after deltas.' },
];
 
const NEXT_STEP_HEADLINE = [
  'Create or select a project',
  'Load your survey data',
  'Run quality checks',
  'Open the cleaning studio',
  'Export or generate a report',
];
 
const NEXT_STEP_DESC = [
  'Every DataQuad workflow starts with a project. It holds your surveys, checks, and cleaned outputs in one place.',
  'Connect KoBoToolbox, SurveyCTO, ODK, or upload a CSV directly. DataQuad handles the schema.',
  'Run all 9 quality detectors across your data in a single pass: completeness, duplicates, outliers, flow, GPS, and more.',
  'Review flagged rows and fix them in the Cleaning Studio. All edits are tracked and reversible.',
  'Export a clean CSV, or generate a full quality report with before/after comparisons.',
];
 
const CTA_LABEL = ['Select project', 'Load data', 'Run checks', 'Clean data', 'Export'];
 
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const statusLabel = (s) => ({
  created: 'new',
  data_loaded: 'loaded',
  raw_quality_done: 'analysed',
  cleaning_in_progress: 'cleaning',
  cleaning_done: 'cleaned',
  cleaned_quality_done: 'assured',
  completed: 'complete',
}[s] || 'new');
 
const statusClass = (s) => ({
  created: 'loaded',
  data_loaded: 'loaded',
  raw_quality_done: 'analysed',
  cleaning_in_progress: 'cleaning',
  cleaning_done: 'cleaning',
  cleaned_quality_done: 'analysed',
  completed: 'complete',
}[s] || 'loaded');
 
const stageToDoneCount = (status) => ({
  created: 1,
  data_loaded: 2,
  raw_quality_done: 3,
  cleaning_in_progress: 3,
  cleaning_done: 4,
  cleaned_quality_done: 4,
  completed: 5,
}[status] || 1);
 
const timeAgo = (iso) => {
  if (!iso) return '';
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return 'just now';
  if (d < 3600) return `${Math.max(1, Math.floor(d / 60))}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  if (d < 604800) return `${Math.floor(d / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};
 
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const WelcomeLanding = ({
  userName,
  userPicture,
  projectLoaded,
  projectName,
  dataLoaded,
  qualityChecksRun,
  cleaningComplete,
  onOpenModal,
  onProjectSelect,
  compact = false,
}) => {
  const { isDarkMode } = useTheme();
  const [recentProjects, setRecentProjects] = useState([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [openingId, setOpeningId] = useState(null);
 
  // Fetch recent projects on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) { if (!cancelled) setProjectsLoaded(true); return; }
        const res = await fetch(`${API_BASE_URL}/api/projects`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        let cloudProjects = [];
        if (res.ok) {
          const data = await res.json();
          cloudProjects = data.projects || [];
        }
        const localProjects = await fetchLocalProjects(false);
        if (!cancelled) {
          setRecentProjects(
            [...localProjects, ...cloudProjects]
              .filter(p => !p.deletedAt && !p.deleted_at)
              .sort((a, b) =>
                new Date(b.updatedAt || b.updated_at || 0) -
                new Date(a.updatedAt || a.updated_at || 0))
              .slice(0, 4)
          );
        }
      } catch (e) {
        console.error('Recent projects fetch failed:', e);
      } finally {
        if (!cancelled) setProjectsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);
 
  const firstName = (userName || '').trim().split(' ')[0] || 'there';
  const hour = new Date().getHours();
  const timeGreeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
 
  // First-time = backend responded, no active or recent projects
  const isFirstTime = projectsLoaded && recentProjects.length === 0 && !projectLoaded;
 
  // Copy selection
  const eyebrow = isFirstTime ? 'Welcome to DataQuad' : 'Welcome back';
  const subtitle = isFirstTime
    ? "Let's turn your messy survey data into something you can trust. It takes about two minutes."
    : projectLoaded
      ? `Picking up on ${projectName || 'your project'}.`
      : "Your data's been waiting patiently. Pick up where we left off.";
 
  // Workflow state
  const doneFlags = [projectLoaded, dataLoaded, qualityChecksRun, cleaningComplete, false];
  const firstIncomplete = doneFlags.findIndex(x => !x);
  const nextIdx = firstIncomplete === -1 ? 4 : firstIncomplete;
  const completedCount = doneFlags.filter(Boolean).length;
  // Rail fill: percentage of the connector line covered by completed stages
  const railFillPct = completedCount <= 1 ? (completedCount === 0 ? 0 : 0) : ((completedCount - 1) / 4) * 100;
 
  const heroKicker = isFirstTime ? 'Step 1 · Getting started' : 'Your next move';
  const heroTitle = isFirstTime ? 'Create your first project' : NEXT_STEP_HEADLINE[nextIdx];
  const heroDesc = isFirstTime
    ? "A project is where everything lives: your raw data, quality checks, and cleaned exports. Let's set one up and take it for a spin."
    : NEXT_STEP_DESC[nextIdx];
  const ctaLabel = isFirstTime ? 'Create project' : CTA_LABEL[nextIdx];
 
  // Recent-project meta counts (for returning users with projects)
  const recentMeta = (() => {
    if (recentProjects.length === 0) return null;
    const cleaning = recentProjects.filter(p =>
      (p.status || '').includes('cleaning')).length;
    const complete = recentProjects.filter(p => p.status === 'completed').length;
    return { total: recentProjects.length, cleaning, complete };
  })();
 
  // Handlers
  const handleStageClick = (idx, target) => {
    onOpenModal(target);
  };
 
  const handleCtaClick = () => {
    onOpenModal(WORKFLOW_STAGES[nextIdx].target);
  };
 
  const handleTrySample = async () => {
    if (sampleLoading) return;
    setSampleLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/sample/create`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) throw new Error(`Sample creation failed (${res.status})`);
      const data = await res.json();
 
      // Preferred path: activate the project directly via the same handler
      // that Projects.js uses.
      if (data.project && onProjectSelect) {
        onProjectSelect(data.project);
        return;
      }
 
      // Fallback: drop the user on the Projects modal.
      if (data.project?.id) {
        localStorage.setItem('activeProjectId', data.project.id);
      }
      onOpenModal('projects');
    } catch (e) {
      console.error('Sample creation failed:', e);
      alert('Could not load sample data. Please try again.');
    } finally {
      setSampleLoading(false);
    }
  };
 
  // D2616 fix: activate the project directly instead of opening the
  // Projects modal. onProjectSelect is App.js's handleProjectSelect —
  // it sets currentProject and state restoration navigates to lastView.
  const handleRecentClick = async (project) => {
    if (openingId) return;
    if (onProjectSelect) {
      setOpeningId(project.id);
      try {
        await activateProjectStorage(project);
      } catch (e) {
        console.error('Failed to start local engine:', e);
        setOpeningId(null);
        return;
      }
      onProjectSelect(project);
      // Clear after a beat in case the parent doesn't unmount this view
      setTimeout(() => setOpeningId(null), 4000);
      return;
    }
    // Fallback for parents that haven't wired onProjectSelect
    localStorage.setItem('activeProjectId', project.id);
    onOpenModal('projects');
  };
 
  return (
    <div className={`dqw ${isDarkMode ? 'dark' : ''} ${compact ? 'compact' : ''}`}>
 
      {/* ── Greeting ── */}
      <div className="dqw-greet">
        <div className="dqw-eyebrow">{eyebrow}</div>
        <h1 className="dqw-h1">
          {isFirstTime ? 'Hello, ' : `${timeGreeting}, `}
          <span className="dqw-name">{firstName}</span>
        </h1>
        <p className="dqw-sub">{subtitle}</p>
      </div>
 
      {/* ── Next-step card ── */}
      <div className="dqw-hero">
        <div className="dqw-hero-body">
          <div className="dqw-kicker">{heroKicker}</div>
          <h2 className="dqw-hero-title">{heroTitle}</h2>
          <p className="dqw-hero-desc">{heroDesc}</p>
          {isFirstTime ? (
            <div className="dqw-meta">
              <span><Clock size={11} /> Takes ~2 min</span>
              <span>No credit card</span>
              <span>Sample data available</span>
            </div>
          ) : recentMeta && (
            <div className="dqw-meta">
              <span><b>{recentMeta.total}</b> recent</span>
              {recentMeta.cleaning > 0 && <span><b>{recentMeta.cleaning}</b> cleaning</span>}
              {recentMeta.complete > 0 && <span><b>{recentMeta.complete}</b> complete</span>}
            </div>
          )}
        </div>
        <div className="dqw-hero-action">
          <button className="dqw-cta" onClick={handleCtaClick} type="button">
            <span>{ctaLabel}</span>
            <ArrowRight size={14} />
          </button>
        </div>
      </div>
 
      {/* ── First-time preview strip ── */}
      {isFirstTime && (
        <div className="dqw-preview">
          {[
            { k: 'Detect', t: 'Issues surfaced automatically' },
            { k: 'Clean',  t: 'Fix flagged rows in the studio' },
            { k: 'Report', t: 'Share quality with stakeholders' },
          ].map(({ k, t }) => (
            <div className="dqw-preview-card" key={k}>
              <span className="dqw-preview-k">{k}</span>
              <span className="dqw-preview-t">{t}</span>
            </div>
          ))}
        </div>
      )}
 
      {/* ── Workflow rail ── */}
      <div className="dqw-flow">
        <div className="dqw-flow-head">
          <span className="dqw-flow-lbl">
            {isFirstTime ? 'Your quality journey' : 'Workflow'}
          </span>
          <span className="dqw-flow-count">
            {isFirstTime ? '5 stages' : `${completedCount} of 5 complete`}
          </span>
        </div>
        <div className="dqw-rail">
          <div className="dqw-rail-line">
            <div className="dqw-rail-fill" style={{ width: `${railFillPct}%` }} />
          </div>
          <div className="dqw-rail-stages">
            {WORKFLOW_STAGES.map((stage, i) => {
              const isDone = doneFlags[i];
              const isCurrent = (i === nextIdx && !isFirstTime) || (i === 0 && isFirstTime);
              return (
                <button
                  key={stage.label}
                  type="button"
                  className={`dqw-stage ${isDone ? 'done' : ''} ${isCurrent ? 'current' : ''}`}
                  onClick={() => handleStageClick(i, stage.target)}
                  title={stage.tip}
                >
                  <span className="dqw-stage-dot">
                    {isDone && <Check size={10} strokeWidth={3.5} />}
                  </span>
                  <span className="dqw-stage-label">{stage.label}</span>
                  {isFirstTime && <span className="dqw-stage-hint">{stage.hint}</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>
 
      {/* ── Bottom: Sample (first-time) OR Recent Projects (returning) ── */}
      {isFirstTime ? (
        <div
          className="dqw-sample"
          onClick={handleTrySample}
          role="button"
          tabIndex={0}
          aria-disabled={sampleLoading}
          onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !sampleLoading) handleTrySample(); }}
          style={{ opacity: sampleLoading ? 0.6 : 1, pointerEvents: sampleLoading ? 'none' : 'auto' }}
        >
          <div className="dqw-sample-icon">
            <Lightbulb size={18} />
          </div>
          <div className="dqw-sample-body">
            <p className="t">Prefer to poke around first?</p>
            <p className="d">Try DataQuad with a sample household survey — 2,847 rows, pre-loaded and ready to analyse.</p>
          </div>
          <div className="dqw-sample-arrow">
            {sampleLoading
              ? <Loader2 size={14} className="dqw-spin" />
              : <ArrowRight size={14} />}
          </div>
        </div>
      ) : projectsLoaded && recentProjects.length > 0 && (
        <div className="dqw-recent">
          <div className="dqw-recent-head">
            <span className="dqw-recent-lbl">Recent projects</span>
            <button type="button" className="dqw-recent-all" onClick={() => onOpenModal('projects')}>
              View all
              <ArrowRight size={11} />
            </button>
          </div>
          <div className="dqw-recent-grid">
            {recentProjects.map((p) => {
              const done = stageToDoneCount(p.status);
              const pct = Math.round((done / 5) * 100);
              const rowCount = p.rowCount || p.row_count;
              const isOpening = openingId === p.id;
              return (
                <div
                  key={p.id}
                  className={`dqw-card ${isOpening ? 'opening' : ''}`}
                  onClick={() => handleRecentClick(p)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleRecentClick(p); }}
                >
                  <div className="dqw-card-top">
                    <p className="dqw-card-title">
                      {p.name}
                      {p.storage_mode === 'local' && (
                        <span title="Stored on this device only" style={{
                          marginLeft: '6px', padding: '1px 6px', borderRadius: '5px',
                          fontSize: '9px', fontWeight: 700, letterSpacing: '0.5px',
                          textTransform: 'uppercase', verticalAlign: 'middle',
                          background: 'rgba(37, 99, 235, 0.12)', color: '#2563EB',
                        }}>local</span>
                      )}
                    </p>
                    {isOpening
                      ? <Loader2 size={13} className="dqw-spin dqw-card-go" />
                      : <ArrowRight size={13} className="dqw-card-go" />}
                  </div>
                  <p className="dqw-card-meta">
                    {timeAgo(p.updatedAt || p.updated_at)}
                    {rowCount ? ` · ${rowCount.toLocaleString()} rows` : ''}
                  </p>
                  <div className="dqw-card-progress">
                    {[0, 1, 2, 3, 4].map((n) => (
                      <span key={n} className={`seg ${n < done ? 'done' : ''}`} />
                    ))}
                  </div>
                  <div className="dqw-card-foot">
                    <span className={`dqw-status ${statusClass(p.status)}`}>
                      {statusLabel(p.status)}
                    </span>
                    <span className="dqw-pct">{pct}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
 
export default WelcomeLanding;